import React, { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Pencil, X, User, Calendar, MapPin, Building2, FileText, GraduationCap,
  Tag, Repeat, Clock, Wallet, Users, Phone, Mail, MessageCircle, ShieldCheck, CheckCircle2,
  AlertTriangle, CreditCard, Loader2, CalendarCheck, CalendarX,
} from "lucide-react";
import { profileIssueLabels } from "@/lib/studentProfile";
import api, { formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";

function ageFromBirthday(birthday) {
  if (!birthday) return null;
  const b = new Date(birthday);
  if (Number.isNaN(b.getTime())) return null;
  const t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) age--;
  return age >= 0 ? age : null;
}

const TABS = [
  { id: "details", label: "Student Details" },
  { id: "enrollment", label: "Enrollment Details" },
  { id: "guardians", label: "Parent/Guardian Info" },
  { id: "accounts", label: "Account Summary" },
];

export default function StudentViewDialog({ student, currency, onClose, onEdit, branchName }) {
  const s = student || {};
  const [tab, setTab] = useState("details");
  const fmt = (v) => `${currency} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const age = useMemo(() => ageFromBirthday(s.birthday), [s.birthday]);

  const classChipLabel = s.class_name
    ? `${s.class_name}${s.section_name ? ` · ${s.section_name}` : ""}`
    : s.individual_class_name
      ? `${s.individual_class_name}`
      : "— · —";

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-4xl rounded-3xl p-0 max-h-[92vh] overflow-hidden flex flex-col bg-[#F6F7FB]"
        data-testid="student-view-dialog"
      >
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 pt-6 pb-5 space-y-5">
          {/* Profile incomplete reminder */}
          {(() => {
            const issues = profileIssueLabels(s);
            if (!issues.length) return null;
            return (
              <div className="rounded-2xl bg-[#FFF8E1] border border-[#FFE082] p-4 flex gap-3" data-testid="student-view-incomplete-banner">
                <AlertTriangle className="w-5 h-5 text-[#E65100] shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-semibold text-[#5D4037]">
                    {s.full_name || "Student"}&apos;s profile is not yet complete
                  </p>
                  <p className="text-sm text-[#5D4037] mt-0.5">
                    Please complete the following: <span className="font-medium">{issues.join(", ")}</span>.
                  </p>
                </div>
                {onEdit && (
                  <Button onClick={onEdit} className="rounded-2xl bg-[#E65100] hover:bg-[#BF360C] text-white shrink-0 h-9" data-testid="student-view-complete-now-btn">
                    Complete now
                  </Button>
                )}
              </div>
            );
          })()}

          {/* Header card */}
          <header className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-5 sm:p-6 flex items-center gap-5">
            <PhotoCircle src={s.photo} alt={s.full_name} />
            <div className="min-w-0 flex-1">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground uppercase">
                {s.full_name || "—"}
              </h2>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <StatusPill status={s.status} />
                <Pill icon={Building2} tone="branch">
                  {branchName || s.branch_name || "—"}
                </Pill>
                <Pill icon={GraduationCap} tone="class">
                  {classChipLabel}
                </Pill>
              </div>
            </div>
          </header>

          {/* Session balance summary — always-visible card (top of dialog). */}
          <SessionBalanceSummary studentId={s.id} branchId={s.branch_id} fmt={fmt} />

          {/* Tabs */}
          <nav className="px-1 flex items-center gap-7 border-b border-brand-purple/10" data-testid="student-view-tabs">
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`student-view-tab-${t.id}`}
                  className={`relative -mb-px pb-3 pt-2 text-sm sm:text-[15px] font-heading transition ${
                    active ? "text-brand-purple font-semibold" : "text-brand-inkMuted hover:text-foreground"
                  }`}
                >
                  {t.label}
                  {active && (
                    <span className="absolute left-0 right-0 -bottom-px h-[3px] bg-brand-purple rounded-t-full" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Tab content */}
          {tab === "details" && <DetailsTab s={s} age={age} branchName={branchName} />}
          {tab === "enrollment" && <EnrollmentTab s={s} fmt={fmt} branchName={branchName} />}
          {tab === "guardians" && <GuardiansTab s={s} />}
          {tab === "accounts" && <AccountsTab studentId={s.id} branchId={s.branch_id} fmt={fmt} />}
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-8 py-4 border-t border-brand-purple/10 bg-white flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-2xl h-11 sm:h-10" data-testid="student-view-close-btn">
            Close
          </Button>
          <Button onClick={onEdit} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 sm:h-10 px-5" data-testid="student-view-edit-btn">
            <Pencil className="w-4 h-4 mr-1.5" /> Edit Student
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------- Tabs ------------------------------------ */
function DetailsTab({ s, age, branchName }) {
  return (
    <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-5 sm:p-7" data-testid="tab-details">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-7">
        <Field icon={User} label="Full Name" value={s.full_name || "—"} />
        <Field icon={Calendar} label="Date of Birth" value={s.birthday || "—"} />
        <Field icon={User} label="Age" value={age !== null ? `${age}` : "—"} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-7 mt-7">
        <Field
          icon={MapPin}
          label="Address"
          value={[s.address, s.city, s.province, s.country].filter(Boolean).join(", ") || "—"}
          className="sm:col-span-2"
        />
        <Field icon={Building2} label="Branch" value={branchName || s.branch_name || "—"} />
      </div>
      <div className="grid grid-cols-1 mt-7">
        <Field icon={FileText} label="General Notes" value={s.general_notes || "None"} />
      </div>
    </section>
  );
}

function EnrollmentTab({ s, fmt, branchName }) {
  const enrollmentType = s.enroll_in_class && s.enroll_in_individual
    ? "Class & Individual Session"
    : s.enroll_in_class
      ? "Class / Section"
      : s.enroll_in_individual
        ? "Individual Session"
        : "Not Enrolled";

  const dateEnrolled = s.created_at ? new Date(s.created_at).toLocaleDateString() : "—";
  const schoolYear = s.school_year_label || s.school_year || "—";

  const netTuition = Math.max(0, Number(s.tuition_fee || 0) - Number(s.tuition_discount || 0));

  return (
    <section className="space-y-5" data-testid="tab-enrollment">
      <div className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-5 sm:p-7">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-7">
          <Field icon={GraduationCap} label="Enrollment Type" value={enrollmentType} />
          <Field icon={Building2} label="Branch" value={branchName || s.branch_name || "—"} />
          <Field icon={Calendar} label="Date Enrolled" value={dateEnrolled} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-7 mt-7">
          <Field icon={Calendar} label="School Year" value={schoolYear} />
          {s.mode_of_payment && <Field icon={Wallet} label="Payment Mode" value={s.mode_of_payment} />}
        </div>
      </div>

      {s.enroll_in_class && (
        <SubCard label="Class / Section">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-7">
            <Field icon={GraduationCap} label="Class / Level" value={s.class_name || "—"} />
            <Field icon={Users} label="Section" value={s.section_name || "—"} />
            <Field icon={Wallet} label="Tuition Fee" value={fmt(s.tuition_fee)} />
          </div>
          {(Number(s.tuition_discount || 0) > 0 || Number(s.reservation_fee || 0) > 0 || Number(s.required_initial_payment || 0) > 0 || Number(s.registration_fee || 0) > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-x-6 gap-y-7 mt-7">
              <Field icon={Wallet} label="Discount" value={fmt(s.tuition_discount)} />
              <Field icon={Wallet} label="Net Tuition" value={fmt(netTuition)} accent />
              <Field icon={Wallet} label="Reservation" value={fmt(s.reservation_fee)} />
              <Field icon={Wallet} label="Initial Payment" value={fmt(s.required_initial_payment)} />
            </div>
          )}
        </SubCard>
      )}

      {s.enroll_in_individual && (
        <SubCard label="Individual Sessions">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-7">
            <Field icon={GraduationCap} label="Session Name" value={s.individual_class_name || "—"} />
            <Field icon={Wallet} label="Fee" value={fmt(s.tuition_fee)} />
          </div>

          <div className="mt-7">
            <p className="text-[11px] font-heading font-semibold uppercase tracking-wider text-brand-inkMuted mb-3">Schedule</p>
            {(s.individual_schedule || []).length === 0 ? (
              <p className="text-sm text-brand-inkMuted italic">No schedule added yet.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="view-individual-schedule">
                {s.individual_schedule.map((sl, idx) => {
                  const slotKey = `${sl.kind || "k"}-${sl.date || sl.day || "x"}-${sl.time_start || ""}-${sl.teacher_id || idx}`;
                  return (
                  <li
                    key={slotKey}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-2xl bg-brand-purpleLight/40 border border-brand-purple/10"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="shrink-0 w-7 h-7 rounded-lg bg-white border border-brand-purple/15 text-brand-purple flex items-center justify-center">
                        {sl.kind === "date" ? <Calendar className="w-3.5 h-3.5" /> : <Repeat className="w-3.5 h-3.5" />}
                      </span>
                      <div className="min-w-0">
                        <p className="font-heading font-semibold text-foreground text-sm">
                          {sl.kind === "date" ? (sl.date || "—") : (sl.day || "—")}
                          {sl.kind === "day" && <span className="text-brand-inkMuted font-normal text-xs ml-1.5">weekly</span>}
                        </p>
                        <p className="text-xs text-brand-inkMuted truncate">
                          {sl.teacher_name ? `Teacher: ${sl.teacher_name}` : "No teacher assigned"}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm tabular-nums text-foreground whitespace-nowrap font-heading">
                      <Clock className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-brand-purple" />
                      {sl.time_start || "—"}{sl.time_end ? ` – ${sl.time_end}` : ""}
                    </span>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        </SubCard>
      )}

      {!s.enroll_in_class && !s.enroll_in_individual && (
        <div className="bg-white rounded-3xl border border-dashed border-brand-purple/20 p-8 text-center text-brand-inkMuted">
          <GraduationCap className="w-7 h-7 mx-auto mb-2 text-brand-purple/60" />
          <p className="font-heading">This student isn&apos;t enrolled yet.</p>
          <p className="text-sm mt-0.5">Edit the student to assign a class, section, or individual session.</p>
        </div>
      )}
    </section>
  );
}

function GuardiansTab({ s }) {
  const guardians = s.guardians || [];
  if (guardians.length === 0) {
    return (
      <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-8 text-center text-brand-inkMuted" data-testid="tab-guardians">
        <Users className="w-7 h-7 mx-auto mb-2 text-brand-purple/60" />
        <p className="font-heading">No guardians recorded for this student.</p>
      </section>
    );
  }
  return (
    <section className="space-y-5" data-testid="tab-guardians">
      {guardians.map((g, idx) => <GuardianCard key={g.id || g.email || g.phone || `g-${idx}`} g={g} idx={idx} />)}
    </section>
  );
}

function GuardianCard({ g, idx }) {
  const initial = (g.name || "?").trim()[0]?.toUpperCase() || "?";
  return (
    <article className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-5 sm:p-7" data-testid={`view-guardian-${idx}`}>
      <header className="flex items-center gap-4 mb-6">
        {g.photo ? (
          <img src={g.photo} alt={g.name} className="w-14 h-14 rounded-full object-cover border border-brand-purple/15" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-brand-purpleLight text-brand-purple flex items-center justify-center font-heading font-bold text-xl">
            {initial}
          </div>
        )}
        <div>
          <p className="font-heading text-xl font-bold uppercase text-foreground tracking-tight">{g.name || "—"}</p>
          <p className="text-sm text-brand-inkMuted mt-0.5">Parent / Guardian</p>
        </div>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-7">
        <Field icon={Users} label="Relationship" value="Parent / Guardian" />
        <Field icon={Phone} label="Contact Number" value={g.phone || "—"} />
        <Field icon={Mail} label="Email Address" value={g.email || "—"} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-7 mt-7">
        <Field icon={MessageCircle} label="FB Messenger" value={g.fb_messenger || "N/A"} />
        <Field icon={ShieldCheck} label="Parent Login Status" value={g.enable_login ? "Enabled" : "No Parent Login"} />
        <Field icon={Clock} label="Invite Sent" value={g.invite_sent_at ? new Date(g.invite_sent_at).toLocaleString() : "—"} />
      </div>
      <div className="grid grid-cols-1 mt-7">
        <Field icon={CheckCircle2} label="Invite Accepted" value={g.invite_accepted_at ? new Date(g.invite_accepted_at).toLocaleString() : "—"} />
      </div>
    </article>
  );
}

/* -------------------------------- Bits ------------------------------------ */
function Field({ icon: Icon, label, value, accent = false, className = "" }) {
  return (
    <div className={className}>
      <p className="text-[11px] font-heading font-semibold uppercase tracking-wider text-brand-inkMuted">{label}</p>
      <div className="mt-1.5 flex items-center gap-2.5 min-w-0">
        <span className="shrink-0 text-brand-inkMuted">
          <Icon className="w-[18px] h-[18px]" />
        </span>
        <span className={`font-heading text-base sm:text-[17px] truncate ${accent ? "text-brand-purple font-semibold" : "text-foreground"}`}>
          {value}
        </span>
      </div>
    </div>
  );
}

function SubCard({ label, children }) {
  return (
    <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-5 sm:p-7">
      <p className="text-[11px] font-heading font-semibold uppercase tracking-wider text-brand-inkMuted mb-5">{label}</p>
      {children}
    </section>
  );
}

function PhotoCircle({ src, alt }) {
  const initials = (alt || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";
  if (src) {
    return (
      <img
        src={src} alt={alt || "Student"}
        className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-[3px] border-brand-purple/15 shadow-sm shrink-0"
      />
    );
  }
  return (
    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-brand-purpleLight text-brand-purple flex items-center justify-center font-heading text-2xl font-bold border-[3px] border-brand-purple/15 shadow-sm shrink-0">
      {initials}
    </div>
  );
}

function StatusPill({ status }) {
  const active = status === "active";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-heading font-medium border ${
        active
          ? "bg-[#E8F8EE] text-[#118D57] border-[#118D57]/25"
          : "bg-[#F4F6FA] text-brand-inkMuted border-brand-purple/15"
      }`}
    >
      <span className={`w-2 h-2 rounded-full ${active ? "bg-[#22C55E]" : "bg-brand-inkMuted/60"}`} />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function Pill({ icon: Icon, tone = "branch", children }) {
  const tones = {
    branch: "border-brand-purple/25 bg-white text-brand-purple",
    class: "border-brand-purple/20 bg-brand-purpleLight/50 text-brand-purple",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-heading font-medium border ${tones[tone] || tones.branch}`}>
      <Icon className="w-3.5 h-3.5" />
      <span className="truncate max-w-[260px]">{children}</span>
    </span>
  );
}

/* ----------------------- Account Summary tab ----------------------- */
const CAT_LABELS = {
  tuition: "Tuition",
  individual_session: "Individual Sessions",
  uniforms: "Uniforms",
  books: "Books",
  events: "Events",
  others: "Others",
};

function SessionBalanceSummary({ studentId, branchId, fmt }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!studentId || !branchId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/student-accounts/summary?branch_id=${branchId}&student_id=${studentId}`);
        if (alive) setSummary(data);
      } catch { /* silent — this card is a top-line summary, never a blocker */ }
    })();
    return () => { alive = false; };
  }, [studentId, branchId]);

  if (!summary) return null;
  const paidRemaining = summary.paid_unattended_sessions || 0;
  const unpaidAttended = summary.unpaid_attended_sessions || 0;
  // Hide the card entirely for students who don't do individual sessions — no
  // point showing two zero chips for tuition-only kids.
  if (paidRemaining === 0 && unpaidAttended === 0) return null;

  const outstanding = summary.total_balance || 0;
  return (
    <section
      data-testid="student-view-session-balance"
      className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-center gap-4 sm:gap-6">
        <div className="flex items-center gap-2 shrink-0">
          <CalendarCheck className="w-5 h-5 text-brand-purple" />
          <span className="font-heading text-sm font-semibold uppercase tracking-wider text-brand-inkMuted">
            Session balance
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="student-view-session-chips">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#E3F2FD] text-[#0D47A1] text-sm font-heading font-bold"
            data-testid="student-view-session-paid"
          >
            <span className="text-base leading-none">🔵</span>
            {paidRemaining} <span className="font-medium opacity-80">paid · unattended</span>
          </span>
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-heading font-bold ${unpaidAttended > 0 ? "bg-[#FDECEA] text-[#C62828]" : "bg-slate-100 text-slate-500"}`}
            data-testid="student-view-session-unpaid"
          >
            <span className="text-base leading-none">🔴</span>
            {unpaidAttended} <span className="font-medium opacity-80">attended · unpaid</span>
          </span>
        </div>
        {outstanding > 0 && (
          <span
            className="ml-auto text-sm text-[#C62828] font-heading font-semibold"
            data-testid="student-view-session-outstanding"
          >
            Outstanding: {fmt(outstanding)}
          </span>
        )}
      </div>
    </section>
  );
}


function AccountsTab({ studentId, branchId, fmt }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [fetched, setFetched] = useState(false);
  const loading = !fetched && !!studentId && !!branchId;

  useEffect(() => {
    if (!studentId || !branchId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/student-accounts/summary?branch_id=${branchId}&student_id=${studentId}`);
        if (!alive) return;
        setSummary(data);
        setError(null);
      } catch (e) {
        if (alive) setError(formatApiError(e));
      } finally {
        if (alive) setFetched(true);
      }
    })();
    return () => { alive = false; };
  }, [studentId, branchId]);

  if (loading) {
    return (
      <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-10 text-center" data-testid="tab-accounts">
        <Loader2 className="w-6 h-6 text-brand-purple animate-spin mx-auto" />
      </section>
    );
  }
  if (error) {
    return (
      <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-6" data-testid="tab-accounts">
        <p className="text-sm text-rose-500">{error}</p>
      </section>
    );
  }
  const s = summary || { total_billed: 0, total_paid: 0, total_balance: 0, by_category: {}, record_count: 0 };
  const owing = s.total_balance > 0;

  return (
    <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-5 sm:p-7 space-y-5" data-testid="tab-accounts">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat tone="blue"  label="Total billed" value={fmt(s.total_billed)} sub={`${s.record_count} record${s.record_count === 1 ? "" : "s"}`} />
        <Stat tone="green" label="Total paid"   value={fmt(s.total_paid)} />
        <Stat tone={owing ? "red" : "neutral"} label="Outstanding" value={fmt(s.total_balance)}
              sub={owing ? "Has a balance" : "Fully settled"} />
      </div>

      {(s.paid_unattended_sessions > 0 || s.unpaid_attended_sessions > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <MiniStat icon={CalendarX} tone="blue"
                    label="Paid but not yet attended"
                    value={s.paid_unattended_sessions}
                    sub="Sessions paid in advance still waiting for the student" />
          <MiniStat icon={CalendarCheck} tone="red"
                    label="Attended but unpaid"
                    value={s.unpaid_attended_sessions}
                    sub="Sessions the student took without paying" />
        </div>
      )}

      <div>
        <h4 className="font-heading font-semibold text-foreground text-sm mb-2 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-brand-purple" />
          Breakdown by category
        </h4>
        <div className="rounded-2xl border border-brand-purple/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FE] text-brand-inkMuted">
              <tr>
                <th className="text-left px-3 py-2 font-heading font-semibold">Category</th>
                <th className="text-center px-3 py-2 font-heading font-semibold">Records</th>
                <th className="text-right px-3 py-2 font-heading font-semibold">Billed</th>
                <th className="text-right px-3 py-2 font-heading font-semibold">Paid</th>
                <th className="text-right px-3 py-2 font-heading font-semibold">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(CAT_LABELS).map(([key, label]) => {
                const c = (s.by_category || {})[key] || { billed: 0, paid: 0, balance: 0, count: 0 };
                if (!c.count) return null;
                return (
                  <tr key={key} className="border-t border-brand-purple/10" data-testid={`accounts-cat-${key}`}>
                    <td className="px-3 py-2 font-heading">{label}</td>
                    <td className="px-3 py-2 text-center tabular-nums">{c.count}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(c.billed)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#2E7D32]">{fmt(c.paid)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-heading font-semibold ${c.balance > 0 ? "text-[#C62828]" : "text-foreground"}`}>{fmt(c.balance)}</td>
                  </tr>
                );
              })}
              {s.record_count === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-brand-inkMuted">No account records yet for this student.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-record table — full list of every Tuition / Session / Uniform / etc. */}
      {(s.records || []).length > 0 && (
        <div data-testid="accounts-records-list">
          <h4 className="font-heading font-semibold text-foreground text-sm mb-2 flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-brand-purple" />
            All records
          </h4>
          <div className="rounded-2xl border border-brand-purple/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F6F8FE] text-brand-inkMuted">
                  <tr>
                    <th className="text-left  px-3 py-2 font-heading font-semibold">Date</th>
                    <th className="text-left  px-3 py-2 font-heading font-semibold">Category</th>
                    <th className="text-left  px-3 py-2 font-heading font-semibold">Description</th>
                    <th className="text-right px-3 py-2 font-heading font-semibold">Amount</th>
                    <th className="text-right px-3 py-2 font-heading font-semibold">Paid</th>
                    <th className="text-right px-3 py-2 font-heading font-semibold">Balance</th>
                    <th className="text-center px-3 py-2 font-heading font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {s.records.map((r) => (
                    <tr key={r.id} className="border-t border-brand-purple/10" data-testid={`accounts-record-${r.id}`}>
                      <td className="px-3 py-2 text-brand-inkMuted whitespace-nowrap">{r.date || "—"}</td>
                      <td className="px-3 py-2 capitalize">{(CAT_LABELS[r.category] || r.category || "other").toString()}</td>
                      <td className="px-3 py-2 max-w-[18rem] truncate" title={r.description}>{r.description || "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(r.amount)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#2E7D32]">{fmt(r.paid_amount)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-heading font-semibold ${r.balance > 0 ? "text-[#C62828]" : "text-foreground"}`}>{fmt(r.balance)}</td>
                      <td className="px-3 py-2 text-center"><RecordStatusBadge record={r} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="text-right">
        <Link to="/students/accounts"
              className="inline-flex items-center gap-1.5 text-xs font-heading font-semibold text-brand-purple hover:underline"
              data-testid="accounts-tab-open-detail">
          Open Student Accounts page →
        </Link>
      </div>
    </section>
  );
}

function Stat({ label, value, sub, tone = "blue" }) {
  const tones = {
    blue:    "from-[#E3F2FD] to-white text-[#0D47A1]",
    green:   "from-[#E8F5E9] to-white text-[#2E7D32]",
    red:     "from-[#FDECEA] to-white text-[#C62828]",
    neutral: "from-slate-50 to-white text-slate-700",
  };
  return (
    <div className={`rounded-2xl border border-slate-200 bg-gradient-to-br p-4 ${tones[tone] || tones.blue}`} data-testid={`accounts-stat-${tone}`}>
      <p className="text-[11px] uppercase tracking-wider font-heading font-semibold opacity-80">{label}</p>
      <p className="mt-1 font-heading font-bold text-xl tabular-nums">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] opacity-70">{sub}</p>}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, sub, tone = "blue" }) {
  const tones = {
    blue: "bg-[#E3F2FD] text-[#0D47A1] border-[#90CAF9]",
    red:  "bg-[#FDECEA] text-[#C62828] border-[#EF9A9A]",
  };
  return (
    <div className={`rounded-2xl border-2 p-3.5 flex items-start gap-3 ${tones[tone]}`}>
      <div className="w-9 h-9 rounded-xl bg-white/60 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-heading font-semibold opacity-80">{label}</p>
        <p className="font-heading font-bold text-xl leading-tight tabular-nums">{value}</p>
        {sub && <p className="text-[11px] mt-0.5 opacity-70">{sub}</p>}
      </div>
    </div>
  );
}

/* Compact one-line status for the per-record table. For individual_session
 * rows it reuses the 4-state attendance-aware semantics (Paid + Attended /
 * Paid Unattended / Unpaid Unattended / Unpaid + Attended). For every other
 * category we fall back to the plain Paid / Partial / Unpaid pill. */
function RecordStatusBadge({ record }) {
  if (record.category === "individual_session") {
    const paid = record.status === "paid";
    const attended = !!record.attended;
    const cfg = paid && attended ?
      { label: "Paid + Attended",   cls: "bg-[#E8F5E9] text-[#2E7D32] border-[#A5D6A7]" }
      : paid && !attended ?
      { label: "Paid Unattended",   cls: "bg-[#E3F2FD] text-[#0D47A1] border-[#90CAF9]" }
      : !paid && !attended ?
      { label: "Unpaid Unattended", cls: "bg-[#FFF8E1] text-[#8D6E00] border-[#FFD54F]" }
      :
      { label: "Unpaid + Attended", cls: "bg-[#FDECEA] text-[#C62828] border-[#EF9A9A]" };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-heading font-semibold border ${cfg.cls}`}>
        {cfg.label}{record.is_makeup && attended ? " · Make-up" : ""}
      </span>
    );
  }
  const fin = record.status === "paid"
    ? { label: "Paid",    cls: "bg-[#E8F5E9] text-[#2E7D32]" }
    : record.status === "partial"
    ? { label: "Partial", cls: "bg-[#FFF3E0] text-[#E65100]" }
    : { label: "Unpaid",  cls: "bg-[#FDECEA] text-[#C62828]" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-heading font-semibold ${fin.cls}`}>
      {fin.label}
    </span>
  );
}

